import { useContext, useMemo } from 'react';
import { QueryApiContext } from './QueryContext';
import { AuthContext } from './AuthContext';
import { useActiveTenant } from './ActiveTenantContext';
import { PermissionsContext } from './PermissionsContext';
import { AuthObject } from '../utils/frontEndTypes';

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const restQueryClient = useContext(QueryApiContext);
  const auth = useContext(AuthContext) as AuthObject;
  const userId = auth.user?.id;
  const userRoles = auth.user?.roles; // Use roles from AuthContext instead of fetching again
  const { activeTenant, isLoading: isActiveTenantLoading } = useActiveTenant();

  // Fetch role types
  const { data: roleTypesData, isLoading: isRoleTypesLoading } = restQueryClient.roleTypes.list.useQuery(
    ['roleTypes'],
    {},
    {
      enabled: !!userId
    }
  );

  const permissions = useMemo(() => {
    const isLoading = isRoleTypesLoading || isActiveTenantLoading;

    if (isLoading || !roleTypesData?.body || !userRoles) {
      return {
        isSiteAdmin: false,
        isTenantOwner: false,
        isTenantAdmin: false,
        isTenantMember: false,
        hasTenantAccess: false,
        isLoading
      };
    }

    const roleTypes = roleTypesData.body || [];

    // Check site admin (not tenant-specific)
    const isSiteAdmin = userRoles.some((role) => {
      const roleType = roleTypes.find((rt) => rt.id === role.roleTypeId);
      return roleType && roleType.scope === 'site' && roleType.role === 'admin';
    });

    // Tenant permissions are scoped to the active tenant
    const isTenantOwner = activeTenant?.role === 'owner';
    const isTenantAdmin = activeTenant?.role === 'admin';
    const isTenantMember = activeTenant?.role === 'member';
    const hasTenantAccess = !!activeTenant;

    return {
      isSiteAdmin,
      isTenantOwner,
      isTenantAdmin,
      isTenantMember,
      hasTenantAccess,
      isLoading: false
    };
  }, [userRoles, roleTypesData, isRoleTypesLoading, isActiveTenantLoading, activeTenant]);

  return <PermissionsContext.Provider value={permissions}>{children}</PermissionsContext.Provider>;
};
